"use client";

import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
interface SearchResult {
  type: "template" | "integration" | "blog";
  title: string;
  url: string;
  description: string;
}
interface SearchResponse {
  results: SearchResult[];
}
const TYPE_LABELS: Record<string, string> = {
  template: "Templates",
  integration: "Integrations",
  blog: "Blog"
};
export function CommandPalette() {
  const router = useRouter();
  const isAuthenticated = useIsAuthenticated();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Open on Cmd+K / Ctrl+K. Authenticated users get the v2 CommandMenu instead
  // (mounted in ConditionalPublicLayout / v2 / contracts layouts), so don't
  // register a second ⌘K listener — both would fire on the same keystroke.
  useEffect(() => {
    if (isAuthenticated) return;
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isAuthenticated]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure the element is mounted
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery("");
      setResults([]);
      setActiveIndex(0);
    }
  }, [isOpen]);
  const fetchResults = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(`/api/public-search?q=${encodeURIComponent(q)}&limit=15`);
      if (res.ok) {
        const data = (await res.json()) as SearchResponse;
        setResults(data.results);
        setActiveIndex(0);
      }
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchResults]);

  // Group results by type for display, flatten for navigation
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    const key = r.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});
  function handleKeyDown_0(e_0: React.KeyboardEvent) {
    if (e_0.key === "Escape") {
      setIsOpen(false);
      return;
    }
    if (e_0.key === "ArrowDown") {
      e_0.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length - 1));
    }
    if (e_0.key === "ArrowUp") {
      e_0.preventDefault();
      setActiveIndex(i_0 => Math.max(i_0 - 1, 0));
    }
    if (e_0.key === "Enter" && results[activeIndex]) {
      e_0.preventDefault();
      setIsOpen(false);
      router.push(results[activeIndex].url);
    }
  }

  // Scroll active item into view
  useEffect(() => {
    if (!resultsRef.current) return;
    const active = resultsRef.current.querySelector("[data-active='true']");
    active?.scrollIntoView({
      block: "nearest"
    });
  }, [activeIndex]);
  if (isAuthenticated || !isOpen) return null;
  let flatIndex = 0;
  return <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]" onClick={() => setIsOpen(false)} role="dialog" aria-modal="true" aria-label="Search B3OS">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" />

      {/* Dialog */}
      <div className="bg-ds-surface-primary border-ds-border relative w-full max-w-lg overflow-hidden rounded-xl border shadow-2xl" onClick={e_1 => e_1.stopPropagation()}>
        {/* Search input */}
        <div className="border-ds-border flex items-center border-b px-4">
          <svg className="text-ds-text-muted mr-2 size-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input ref={inputRef} type="text" value={query} onChange={e_2 => setQuery(e_2.target.value)} onKeyDown={handleKeyDown_0} placeholder="Search templates, integrations..." className="text-ds-text-primary placeholder:text-ds-text-muted w-full bg-transparent py-3 text-sm outline-none" aria-label="Search" />
          <kbd className="text-ds-text-muted border-ds-border ml-2 shrink-0 rounded border px-1.5 py-0.5 font-mono text-xs">
            esc
          </kbd>
        </div>

        {/* Results */}
        <div ref={resultsRef} className="max-h-80 overflow-y-auto">
          {isLoading && <div className="text-ds-text-muted p-4 text-center text-sm">Searching...</div>}
          {!isLoading && query.length >= 2 && results.length === 0 && <div className="text-ds-text-muted p-4 text-center text-sm">No results found</div>}
          {!isLoading && query.length < 2 && <div className="text-ds-text-muted p-4 text-center text-sm">Type at least 2 characters to search</div>}
          {!isLoading && Object.entries(grouped).map(([type, items]) => <div key={type}>
                <div className="text-ds-text-muted bg-ds-surface-secondary px-4 py-1.5 text-xs font-medium uppercase">
                  {TYPE_LABELS[type] || type}
                </div>
                {items.map(item => {
            const idx = flatIndex++;
            const isActive = idx === activeIndex;
            return <Link key={item.url} href={item.url} data-active={isActive} className={`block px-4 py-2.5 transition-colors ${isActive ? "bg-ds-surface-secondary" : "hover:bg-ds-surface-secondary"}`} onClick={() => setIsOpen(false)}>
                      <div className="text-ds-text-primary text-sm font-medium">{item.title}</div>
                      {item.description && <div className="text-ds-text-muted mt-0.5 line-clamp-1 text-xs">{item.description}</div>}
                    </Link>;
          })}
              </div>)}
        </div>

        {/* Footer hint */}
        <div className="border-ds-border text-ds-text-muted flex items-center gap-4 border-t px-4 py-2 text-xs">
          <span>
            <kbd className="border-ds-border rounded border px-1 py-0.5 font-mono">↑</kbd>{" "}
            <kbd className="border-ds-border rounded border px-1 py-0.5 font-mono">↓</kbd> Navigate
          </span>
          <span>
            <kbd className="border-ds-border rounded border px-1 py-0.5 font-mono">Enter</kbd> Open
          </span>
          <span>
            <kbd className="border-ds-border rounded border px-1 py-0.5 font-mono">Esc</kbd> Close
          </span>
        </div>
      </div>
    </div>;
}